import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { CharacterMood, CharacterName, characters } from '@/components/character';

/**
 * CharacterContext — state för NPF-barnvyns kompanjon (Leonardo m.fl.).
 *
 * Mönster följer AppContext: createContext med defaultvärde, *Scene-provider
 * som memoiserar value, och en useContext-hook (useCharacter).
 *
 * Persistens (P0): selectedCharacter + characterName lagras i localStorage under
 * nyckeln 'tomorrow_character'. mood är ett runtime-tillstånd (sparas inte) som
 * sätts kontextuellt av vyerna via setMood/celebrate.
 *
 * Providern monteras enbart i barnvyns gren (AuthRouter, när effectiveChildMode
 * är på). Vuxen-/handledarflödet rör aldrig denna context.
 */

const STORAGE_KEY = 'tomorrow_character';

type PersistedCharacter = {
  selectedCharacter: CharacterName | null;
  characterName: string;
};

const readPersisted = (): PersistedCharacter => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { selectedCharacter: null, characterName: '' };
    const parsed = JSON.parse(raw) as Partial<PersistedCharacter>;
    const selected = parsed.selectedCharacter ?? null;
    // Validera att den lagrade karaktären fortfarande finns i listan.
    const valid = selected && characters.some((c) => c.name === selected) ? selected : null;
    return {
      selectedCharacter: valid,
      characterName: typeof parsed.characterName === 'string' ? parsed.characterName : '',
    };
  } catch {
    return { selectedCharacter: null, characterName: '' };
  }
};

export type CharacterContextType = {
  /** Vald karaktär, eller null innan onboarding körts. */
  selectedCharacter: CharacterName | null;
  /** Barnets valda namn på karaktären (förifyllt från defaultName i onboarding). */
  characterName: string;
  /** Runtime-humör som styr maskotens uttryck. Default 'idle'. */
  mood: CharacterMood;
  /** Sätt humör kontextuellt (t.ex. 'celebrate' när en aktivitet skapas). */
  setMood: (mood: CharacterMood) => void;
  /**
   * Runtime-text för den alltid-synliga kompanjonens pratbubbla. Tom sträng =
   * ingen bubbla. Sätts kontextuellt av vyerna (hemskärm, röst, skapa-wizard)
   * och nollställs vid unmount/stängning så att texten inte fastnar.
   */
  companionSpeech: string;
  /** Sätt kompanjonens bubbeltext (tom sträng döljer bubblan). */
  setCompanionSpeech: (speech: string) => void;
  /** Spara karaktärsval + namn (persisteras). Anropas från onboarding. */
  chooseCharacter: (character: CharacterName, name: string) => void;
  /** Nollställ valet (kör om onboarding). */
  resetCharacter: () => void;
};

const defaults = readPersisted();

export const CharacterContext = createContext<CharacterContextType>({
  selectedCharacter: null,
  characterName: '',
  mood: 'idle',
  setMood: () => {},
  companionSpeech: '',
  setCompanionSpeech: () => {},
  chooseCharacter: () => {},
  resetCharacter: () => {},
});

type props = {
  children?: React.ReactNode;
};

export const CharacterScene = ({ children }: props) => {
  const [selectedCharacter, setSelectedCharacter] = useState<CharacterName | null>(
    defaults.selectedCharacter,
  );
  const [characterName, setCharacterName] = useState<string>(defaults.characterName);
  const [mood, setMood] = useState<CharacterMood>('idle');
  const [companionSpeech, setCompanionSpeech] = useState<string>('');

  const persist = useCallback((character: CharacterName | null, name: string) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ selectedCharacter: character, characterName: name }),
    );
  }, []);

  const chooseCharacter = useCallback(
    (character: CharacterName, name: string) => {
      const trimmed = name.trim();
      const fallback = characters.find((c) => c.name === character)?.defaultName ?? '';
      const finalName = trimmed.length > 0 ? trimmed : fallback;
      setSelectedCharacter(character);
      setCharacterName(finalName);
      persist(character, finalName);
    },
    [persist],
  );

  const resetCharacter = useCallback(() => {
    setSelectedCharacter(null);
    setCharacterName('');
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const contextProvider = useMemo(
    () => ({
      selectedCharacter,
      characterName,
      mood,
      setMood,
      companionSpeech,
      setCompanionSpeech,
      chooseCharacter,
      resetCharacter,
    }),
    [selectedCharacter, characterName, mood, companionSpeech, chooseCharacter, resetCharacter],
  );

  return <CharacterContext.Provider value={contextProvider}>{children}</CharacterContext.Provider>;
};

export const useCharacter = () => useContext(CharacterContext);
